import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Stack,
  Alert,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import { enqueueSnackbar } from "notistack";
import { useAuth } from "../../hooks";
import PVLCVideoPlayer from "../LiveStream/Heart/PVLCVideoPlayer";

const VideoController = ({ zg }: { zg: ZegoExpressEngine | null }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputUrl, setInputUrl] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<any>(null);
  const { channel_name } = useParams();
  const [currentTime, setCurrentTime] = useState(0);
  const { userInfo } = useAuth();
  const [duration, setDuration] = useState(0);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [pvlcUrl, setPvlcUrl] = useState("");
  const [startedVideo, setStartedVideo] = useState(false);
  const [broadcastError, setBroadcastError] = useState("");

  const handleAdd = () => {
    setInputUrl("");
    setIsEditing(true);
  };

  const handleEdit = () => {
    setInputUrl(pvlcUrl || "");
    setIsEditing(true);
  };

  // Enhanced broadcast message function with error handling
  const sendBroadcastMessage = async (type: string, data: any) => {
    if (!zg) {
      console.warn("⚠️ Engine not ready for broadcasting");
      setBroadcastError("Video engine not ready");
      return false;
    }

    if (!channel_name) {
      console.warn("⚠️ No channel name available");
      setBroadcastError("Channel not available");
      return false;
    }

    const message = {
      type,
      data,
      userId: userInfo?.id,
      userName: userInfo?.username,
      timestamp: Date.now(),
      roomId: channel_name,
    };

    console.log("📡 Sending broadcast message:", type, data);

    try {
      const result = await zg.sendBroadcastMessage(
        channel_name,
        JSON.stringify(message)
      );
      console.log("✅ Broadcast sent:", result);
      setBroadcastError(""); // Clear any previous errors
      
      // Show success notification for important actions
      if (['start', 'play', 'pause', 'end'].includes(data.action)) {
        enqueueSnackbar(`Video ${data.action} command sent to all participants`, { 
          variant: 'success' 
        });
      }
      
      return true;
    } catch (error: any) {
      console.error("❌ Broadcast message error:", error || error.message);
      // setBroadcastError(`Failed to send ${data.action} command`);
      // enqueueSnackbar(`Failed to send ${data.action} command to participants`, { 
      //   variant: 'error' 
      // });
      return false;
    }
  };

  // Sync interval for video timing
  useEffect(() => {
    if (!startedVideo || !isPlaying) return;
    
    const timeInterval = setInterval(() => {
      if (startedVideo && isPlaying && playerRef.current) {
        sendBroadcastMessage("player_control", {
          action: "sync",
          currentTime: playerRef.current.currentTime || 0,
          timestamp: Date.now(),
          duration: duration,
          isPlaying: isPlaying,
        });
      }
    }, 3000); // Increased to 3 seconds to reduce network load
    
    return () => clearInterval(timeInterval);
  }, [startedVideo, isPlaying, duration]);

  const handleVideoStart = async () => {
    if (!pvlcUrl) {
      enqueueSnackbar("Please add a video URL first", { variant: "warning" });
      return;
    }

    const success = await sendBroadcastMessage("player_control", {
      action: "start",
      currentTime: playerRef.current?.currentTime || 0,
      timestamp: Date.now(),
      duration: duration,
      videoUrl: pvlcUrl
    });

    if (success) {
      setStartedVideo(true);
      console.log("Video session started:", pvlcUrl);
    }
  };

  const handleVideoEnd = async () => {
    const success = await sendBroadcastMessage("player_control", {
      action: "end",
      currentTime: playerRef.current?.currentTime || 0,
      timestamp: Date.now(),
      duration: duration,
    });

    if (success) {
      setIsPlaying(false);
      setStartedVideo(false);
      setCurrentTime(0);
      console.log("Video session ended");
    }
  };

  const handleVideoPlay = async () => {
    if (!startedVideo) {
      enqueueSnackbar("Please start the video session first", { variant: "warning" });
      return;
    }

    const success = await sendBroadcastMessage("player_control", {
      action: "play",
      currentTime: playerRef.current?.currentTime || 0,
      timestamp: Date.now(),
      duration: duration,
    });

    if (success) {
      setIsPlaying(true);
      console.log("Video playback started");
    }
  };

  const handleVideoTimeUpdate = () => {
    const video = playerRef.current;
    if (video) {
      setCurrentTime(video.currentTime);
      if (video.duration && video.duration !== duration) {
        setDuration(video.duration);
      }
    }
  };

  const handleVideoPause = async () => {
    const success = await sendBroadcastMessage("player_control", {
      action: "pause",
      currentTime: playerRef.current?.currentTime || 0,
      timestamp: Date.now(),
      duration: duration,
    });

    if (success) {
      setIsPlaying(false);
      console.log("Video paused");
    }
  };

  const handleVideoSeek = async () => {
    if (!startedVideo) return;
    
    console.log("Seeking to:", playerRef.current?.currentTime);
    await sendBroadcastMessage("player_control", {
      action: "seek",
      currentTime: playerRef.current?.currentTime || 0,
      timestamp: Date.now(),
      duration: duration,
      isPlaying: isPlaying,
    });
  };

  const handleTimeLine = (e: any, value: number | number[]) => {
    if (!playerRef.current || !duration) return;
    
    const seekVal = Array.isArray(value) ? value[0] : value;
    const newTime = (seekVal / 100) * duration;
    playerRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVideoProgress = (playedSeconds: number) => {
    setCurrentTime(playedSeconds);
    
    // Reduced sync frequency to every 10 seconds during progress
    if (Math.floor(playedSeconds) % 10 === 0) {
      sendBroadcastMessage("player_sync", {
        currentTime: playedSeconds,
        isPlaying,
        duration: duration,
      });
    }
  };

  const handleVideoLoadedMetadata = () => {
    const video = playerRef.current;
    if (video && video.duration) {
      setDuration(video.duration);
      setIsVideoReady(true);
      console.log("📹 Video metadata loaded, duration:", video.duration);
    }
  };

  const handleVideoError = (error: any) => {
    console.error("Video playback error:", error);
    enqueueSnackbar("Video playback error occurred", { variant: "error" });
    setBroadcastError("Video playback error");
  };

  const handleSave = () => {
    if (!inputUrl.trim()) {
      enqueueSnackbar("Please enter a valid video URL", { variant: "warning" });
      return;
    }

    // Basic URL validation
    try {
      new URL(inputUrl);
    } catch (error) {
      enqueueSnackbar("Please enter a valid URL", { variant: "warning" });
      return;
    }

    setPvlcUrl(inputUrl.trim());
    setIsEditing(false);
    setStartedVideo(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setBroadcastError("");
    enqueueSnackbar("Video URL updated successfully", { variant: "success" });
  };

  const handleCancel = () => {
    setInputUrl("");
    setIsEditing(false);
  };

  // Reset video state when URL changes
  useEffect(() => {
    if (pvlcUrl) {
      setStartedVideo(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setIsVideoReady(false);
    }
  }, [pvlcUrl]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        background: "#232326",
        borderRadius: 2,
        p: 3,
        color: "#fff",
        mt: 2,
        boxShadow: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        PVLC Video Manager
      </Typography>

      {/* Error Alert */}
      {broadcastError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {broadcastError}
        </Alert>
      )}

      {!pvlcUrl ? (
       <Button
  variant="contained"
  onClick={handleAdd}
  sx={{
    width: "100%",
    py: 1.75,
    fontWeight: 800,
    textTransform: "none",
    letterSpacing: "0.01em",
    borderRadius: 2,                     // ~16px
    bgcolor: "#E50914",                  // Netflix red
    color: "#fff",
    boxShadow: "0 8px 24px rgba(229,9,20,0.35)",
    "&:hover": {
      bgcolor: "#b20710",
      boxShadow: "0 12px 28px rgba(229,9,20,0.45)",
    },
    "&:active": {
      transform: "translateY(1px)",
      boxShadow: "0 6px 18px rgba(229,9,20,0.35)",
    },
    "&:focus-visible": {
      outline: "2px solid rgba(229,9,20,0.6)",
      outlineOffset: "2px",
    },
    "&.Mui-disabled": {
      bgcolor: "rgba(229,9,20,0.35)",
      color: "rgba(255,255,255,0.7)",
      boxShadow: "none",
    },
  }}
>
  Add PVLC (Video Link)
</Button>

      ) : (
        <>
          {/* Video Player */}
          <Box sx={{ mb: 2 }}>
            <PVLCVideoPlayer
              playerRef={playerRef}
              url={pvlcUrl}
              isHost={true}
              isLarge={false}
              playing={isPlaying}
              handleVideoProgress={handleVideoProgress}
              handleVideoSeek={handleVideoSeek}
              handleTimeLine={handleTimeLine}
              handleVideoTimeUpdate={handleVideoTimeUpdate}
              handleVideoLoadedMetadata={handleVideoLoadedMetadata}
              playbackRate={1}
              currentTime={currentTime}
              duration={duration}
            />
          </Box>

          {/* Video Info */}
          <Box sx={{ mb: 2, p: 2, bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong> {
                !startedVideo ? "Ready to start" :
                isPlaying ? "Playing" : "Paused"
              }
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Duration:</strong> {
                duration ? `${Math.floor(duration / 60)}:${String(Math.floor(duration % 60)).padStart(2, '0')}` : "Loading..."
              }
            </Typography>
            <Typography variant="body2">
              <strong>Current Time:</strong> {
                `${Math.floor(currentTime / 60)}:${String(Math.floor(currentTime % 60)).padStart(2, '0')}`
              }
            </Typography>
          </Box>

          {/* Control Buttons */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleVideoStart}
              disabled={startedVideo || !isVideoReady}
              sx={{ minWidth: "80px" }}
            >
              {!isVideoReady ? "Loading..." : "Start Session"}
            </Button>
            
            <Button
              variant="contained"
              color="success"
              onClick={handleVideoPlay}
              disabled={isPlaying || !startedVideo}
              sx={{ minWidth: "80px" }}
            >
              Play
            </Button>
            
            <Button
              variant="contained"
              color="warning"
              onClick={handleVideoPause}
              disabled={!isPlaying || !startedVideo}
              sx={{ minWidth: "80px" }}
            >
              Pause
            </Button>
            
            <Button
              variant="contained"
              color="error"
              onClick={handleVideoEnd}
              disabled={!startedVideo}
              sx={{ minWidth: "80px" }}
            >
              End Session
            </Button>
            
            <Button 
              variant="outlined" 
              color="info" 
              onClick={handleEdit}
              sx={{ minWidth: "80px" }}
            >
              Change Video
            </Button>
          </Stack>

          {/* Instructions */}
          <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.3)", borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: "#ccc" }}>
              📝 <strong>Instructions:</strong><br/>
              1. Click "Start Session" to begin video mode for all participants<br/>
              2. Use "Play/Pause" to control playback for everyone<br/>
              3. Click "End Session" to return to live video call mode
            </Typography>
          </Box>
        </>
      )}

      {/* Edit/Add Dialog */}
      <Dialog
  open={isEditing}
  onClose={handleCancel}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: (theme) => ({
      borderRadius: { xs: 2, sm: 3 },
      backgroundColor: "#141414",
      color: "#fff",
      border: "1px solid rgba(255,255,255,0.14)",
      boxShadow: "0 22px 70px rgba(0,0,0,0.6)",
      p: { xs: 1, sm: 2 },
    }),
  }}
>
  <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
    {pvlcUrl ? "Change PVLC Video Link" : "Add PVLC Video Link"}
  </DialogTitle>

  <DialogContent sx={{ pt: 0.5 }}>
    <TextField
      autoFocus
      margin="dense"
      label="Video Link (YouTube, Vimeo, etc.)"
      type="url"
      fullWidth
      value={inputUrl}
      onChange={(e) => setInputUrl(e.target.value)}
      placeholder="https://www.youtube.com/watch?v=..."
      helperText="Enter a valid video URL from YouTube, Vimeo, or other supported platforms"
      sx={{
        mt: 1,
        "& .MuiOutlinedInput-root": {
          borderRadius: 1.5,
          backgroundColor: "#1a1a1a",
          color: "#fff",
          "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.28)" },
          "&.Mui-focused fieldset": {
            borderColor: "#E50914",
            boxShadow: "0 0 0 2px rgba(229,9,20,0.2)",
          },
        },
        "& .MuiInputLabel-root": {
          color: "rgba(255,255,255,0.72)",
          "&.Mui-focused": { color: "#ffffff" },
        },
        "& .MuiFormHelperText-root": {
          color: "rgba(255,255,255,0.65)",
        },
      }}
    />
  </DialogContent>

  <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
    <Button
      onClick={handleCancel}
      sx={{
        textTransform: "none",
        fontWeight: 700,
        borderRadius: 1.5,
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.18)",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
      }}
    >
      Cancel
    </Button>

    <Button
      onClick={handleSave}
      disabled={!inputUrl.trim()}
      variant="contained"
      sx={{
        textTransform: "none",
        fontWeight: 700,
        borderRadius: 1.5,
        bgcolor: "#E50914",
        boxShadow: "0 10px 22px rgba(229,9,20,0.28)",
        "&:hover": {
          bgcolor: "#b20710",
          boxShadow: "0 12px 26px rgba(229,9,20,0.36)",
        },
        "&.Mui-disabled": {
          bgcolor: "rgba(255,255,255,0.2)",
          color: "rgba(255,255,255,0.55)",
        },
      }}
    >
      {pvlcUrl ? "Update" : "Add"}
    </Button>
  </DialogActions>
</Dialog>

    </Box>
  );
};

export default VideoController;